import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Dialog } from "primereact/dialog";
import { JVDropdown } from "../../../shared/JVDropdown";
import { JVInput } from "../../../shared/JVInput";
import { Button } from "primereact/button";
import { Fetch } from "../../../shared/Fetch";
import { JVDropdownInput } from "../../../shared/JVDropdownInput";
import { JVInputMask } from "../../../shared/JVInputMask";
import { JVInputCheck } from "../../../shared/JVInputCheck";
import { ITenderClientPOC } from "./Tender_Client_POC";
import { IDesignationMastDropdown } from "../../../models/IDropdown";
import { ILogin } from "../../../models/Login";

interface IProps {
  singleData?: ITenderClientPOC;
  open: boolean;
  hideDialog: () => void;
  toast: any;
  reload: boolean;
  setReload: (is: boolean) => void;
}

const schema = yup.object({
  contact_Person_EmailID: yup.string().required("EmailID is required*").email(),
  vendor_Company: yup
    .object()
    .shape({
      vendor_Company_ID: yup.number().min(1, "Client Company Name is required*").required("Client Company Name is required*"),
      vendor_Company_Name: yup.string().required(),
      website_URL: yup.string(),
    })
    .typeError("Client Company Name is required"),
  contact_Person_Name: yup
    .string()
    .required("Name is required*")
    .min(3)
    .matches(/^[A-Za-z\s.-]+$/, "Only alphabets are allowed"),
  designation: yup.number().min(1, "Designation is required*").required("Designation is required*"),
  phone_No: yup.string().required("Phone No is required*"),
  cell_No: yup.string().nullable(),
  department: yup.number().min(1, "Department is Required").required("Department is Required"),
  ext: yup
    .string()
    .matches(/^\d{1,6}$/, "Extension must be 1 to 6 digits")
    .nullable(),
  major_Technology: yup
    .string()
    .nullable()
    .matches(/^[A-Za-z0-9\s#+.,/-]+$/, "Invalid characters"),
  // department: yup.string().required("Department is Required"),
});

const emailSchema = yup.object().shape({
  email: yup.string().required().email(),
});

const emptyForm: Partial<ITenderClientPOC> & Record<string, any> = {
  contact_Person_ID: 0,
  vendor_Company_ID: 0,
  vendor_Company: {
    vendor_Company_ID: 0,
    vendor_Company_Name: "",
  },
  Vendor_Company_ID: 0,
  vendor_Company_Name: "",
  contact_Person_EmailID: "",
  contact_Person_Name: "",
  designation: 0,
  designation_Name: "",
  department: 0,
  phone_No: "",
  cell_No: "",
  add_by_User: 0,
  major_Technology: "",
  ext: "",
};

export const Form = (props: IProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [clients, setClients] = useState<{ id: number; client_Name: string }[]>([]);
  const [designations, setDesignations] = useState<any[]>([]);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [deparment, setDeparment] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    getValues,
    watch,
  } = useForm<ITenderClientPOC>({
    defaultValues: emptyForm as any,
    resolver: yupResolver(schema),
    shouldFocusError: false,
  });

  const emailValue = watch("contact_Person_EmailID");

  const loadDesignations = async () => {
    const response = await Fetch("Dropdown/designation", "GET", null, null, null);
    if (response) {
      const data: Array<IDesignationMastDropdown> = await response.json();
      setDesignations(data);
    }
  };

  const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
  let _roles: Array<number> = [];
  loginObj.roles.forEach((role) => {
    _roles.push(role.role_ID);
  });

  // Simplified: no category filtering on this form, just search all companies by name.
  const loadClients = async (search?: string) => {
    const response = await Fetch("Tender_POC_Mast/getClientsByCategory", "GET", null, search ? `filter=${search}` : null, null);
    if (response?.ok) {
      const data = await response.json();
      setClients(data);
    }
  };

  const completeMethod = (e: { query: string }) => {
    loadClients(e?.query);
  };

  useEffect(() => {
    if (verifiedEmail !== null && emailValue !== verifiedEmail) {
      setIsEmailVerified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue]);

  const onSubmit = async (data: ITenderClientPOC) => {
    try {
      if (!isEmailVerified) {
        props.toast.current.show({ severity: "warn", summary: "Verify Email", detail: "Please click the Check Email button before saving." });
        return;
      }
      setLoading(true);

      //   delete (data as any).contact_Person_ID;
      const isEdit = !!props.singleData?.contact_Person_ID;
      if (isEdit && !isDirty) {
        props.toast.current.show({ severity: "info", summary: "No Changes", detail: "Nothing was changed." });
        props.hideDialog();
        return;
      }

      data.Vendor_Company_ID = (data?.vendor_Company as any)?.vendor_Company_ID ?? 0;
      delete data.vendor_Company;

      const response = isEdit ? await Fetch(`Tender_POC_Mast`, "PUT", data, null, props.toast) : await Fetch("Tender_POC_Mast", "POST", data, null, props.toast);

      if (response?.ok) {
        setIsEmailVerified(false);
        props.toast.current.show({ severity: "success", summary: "Success", detail: isEdit ? "RFP Client Updated Successfully" : "RFP Client Added Successfully" });
        props.setReload(!props.reload);
        props.hideDialog();
        reset(emptyForm as any);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onHide = () => {
    setIsEmailVerified(false);
    props.hideDialog();
    reset(emptyForm as any);
  };

  const loadDepartment = async () => {
    const response = await Fetch("RFP_POC_Department_Mast/GetActiveDepartment", "GET", null, null, props.toast);
    if (response?.ok) {
      const data: Array<any[]> = await response.json();
      setDeparment(data);
    }
  };

  useEffect(() => {
    loadDesignations();
    loadDepartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    if (props.open && props.singleData) {
      const existingEmail: string = (props.singleData as any).contact_Email ?? "";
      reset({
        contact_Person_ID: (props.singleData as any).contact_Person_ID ?? 0,

        vendor_Company: {
          vendor_Company_ID: (props.singleData as any).vendor_Company_ID ?? 0,
          vendor_Company_Name: (props.singleData as any).tender_Client_Name ?? "",
        },

        contact_Person_Name: (props.singleData as any).contact_Name ?? "",
        contact_Person_EmailID: (props.singleData as any).contact_Email ?? "",
        phone_No: (props.singleData as any).contact_Mobile ?? "",
        cell_No: (props.singleData as any).contact_Phone ?? "",
        // department: (props.singleData as any).department ?? "",
        department: (props.singleData as any).department_ID ?? 0,
        designation: (props.singleData as any).designation ?? 0,
        major_Technology: (props.singleData as any).major_Technology ?? "",
        ext: (props.singleData as any).ext ?? "",
      });
      setIsEmailVerified(true);
      setVerifiedEmail(existingEmail);
    } else {
      reset(emptyForm as any);
      setIsEmailVerified(false);
      setVerifiedEmail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.singleData]);

  const checkEmail = async () => {
    const email = getValues("contact_Person_EmailID");
    const valid = await emailSchema.isValid({ email });
    if (!valid) {
      props.toast.current.show({
        severity: "error",
        summary: "Please Enter a Valid Email",
      });
      return;
    }

    if (valid) {
      setLoading(true);
      const response = await Fetch("Tender_POC_Mast/searchEmail", "GET", null, `filter=${email}`, props.toast);
      if (response?.ok) {
        setIsEmailVerified(true);
        setVerifiedEmail(email ?? null);
        props.toast.current.show({ severity: "success", summary: "Email Available" });
      }
      setLoading(false);
    } else {
      props.toast.current.show({ severity: "error", summary: "Please Enter a Valid Email" });
    }
  };

  return (
    <Dialog visible={props.open} style={{ width: "80vw" }} header="Add/Edit Client POC" modal className="p-fluid" onHide={onHide}>
      <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
        <JVInputCheck required func={checkEmail} label="Email ID" control={control} name="contact_Person_EmailID" error={errors.contact_Person_EmailID?.message} disabled={!!props.singleData?.contact_Person_ID && !(_roles.includes(1) || _roles.includes(26) || _roles.includes(56))} />
        <JVDropdownInput required completeMethod={completeMethod} label="Client Company Name" control={control} name="vendor_Company" error={errors.vendor_Company?.message} options={clients} optionLabel="vendor_Company_Name" placeholder="Search Client Company" disabled={!!props.singleData?.contact_Person_ID && !(_roles.includes(1) || _roles.includes(26) || _roles.includes(56))} />
        <JVInput required label="Name" control={control} name="contact_Person_Name" error={errors.contact_Person_Name?.message} />
        <JVDropdown required label="Designation" control={control} name="designation" error={errors.designation?.message} options={designations} optionLabel="designation_Name" optionValue="designationId" placeholder="Select Designation" />
        <JVDropdown required label="Department" control={control} name="department" error={errors.department?.message} options={deparment} optionLabel="department" optionValue="id" placeholder="Select Deparment" />
        <JVInputMask mask="(999) 999-9999" required label="Phone No" control={control} name="phone_No" error={errors.phone_No?.message} />
        <JVInput label="Ext." control={control} name="ext" error={errors.ext?.message} />
        {/* <JVInput label="Cell No" control={control} name="cell_No" error={errors.cell_No?.message} /> */}
        <JVInputMask mask="(999) 999-9999" label="Cell No" control={control} name="cell_No" error={errors.cell_No?.message} />
        <JVInput label="Major Technology" control={control} name="major_Technology" error={errors.major_Technology?.message} />
        <div className="col-12 md:col-12 flex gap-4 justify-content-end">
          <Button style={{ width: "100px" }} label="SAVE" type="submit" loading={loading} disabled={!isDirty}></Button>
          <Button style={{ width: "100px" }} label="CLOSE" onClick={onHide} type="button" className="p-button-outlined surface-200"></Button>
        </div>
      </form>
    </Dialog>
  );
};
